import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";

function upsertMeta(attr, key, content) {
  if (content == null || content === "") return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Mounted once: applies per-page SEO meta tags (title, description, OG, canonical)
// from the admin-editable SEO records, falling back to site-level defaults.
export const SeoManager = () => {
  const location = useLocation();
  const { settings } = useSite();
  const seoMap = useRef({});

  useEffect(() => {
    api.get("/public/seo").then((r) => {
      const m = {};
      (r.data || []).forEach((p) => { if (p.path) m[p.path] = p; });
      seoMap.current = m;
      apply();
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = useCallback(() => {
    const path = location.pathname;
    const rec = seoMap.current[path] || null;

    const title = (rec && rec.title) || settings.seo_default_title || settings.brand_name || "Trilogy Sports & Fitness";
    const desc = (rec && rec.meta_description) || settings.seo_default_description || "";
    const ogTitle = (rec && rec.og_title) || title;
    const ogDesc = (rec && rec.og_description) || desc;
    const ogImg = (rec && rec.og_image) || settings.seo_default_og_image || settings.hero_poster_url || "";
    const keywords = (rec && rec.keywords) || settings.seo_keywords || [];

    document.title = title;
    upsertMeta("name", "description", desc);
    if (Array.isArray(keywords) && keywords.length) upsertMeta("name", "keywords", keywords.join(", "));

    upsertMeta("property", "og:site_name", settings.brand_name || "Trilogy Sports & Fitness");
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", ogTitle);
    upsertMeta("property", "og:description", ogDesc);
    upsertMeta("property", "og:url", window.location.href);
    if (ogImg) upsertMeta("property", "og:image", ogImg);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", ogTitle);
    upsertMeta("name", "twitter:description", ogDesc);
    if (ogImg) upsertMeta("name", "twitter:image", ogImg);

    upsertLink("canonical", window.location.href.split("?")[0]);

    if (settings.search_console_verification) {
      upsertMeta("name", "google-site-verification", settings.search_console_verification);
    }
  }, [location.pathname, settings]);

  useEffect(() => { apply(); }, [apply]);
  return null;
};
